import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import flowerIcon from '../img/flowers.svg';

// Repli tant que le header n'a pas ete mesure.
const HEADER_FALLBACK = 56;

// Remontee du bloc au-dessus du centre, en part de la hauteur visible.
// Le centre geometrique tombe trop bas a l'oeil : le regard attend l'oeuvre
// un peu plus haut. Mobile uniquement, le desktop est deja juste.
// Decalage vertical du bloc, en part de la hauteur visible. Negatif = vers le
// bas. Mobile uniquement : le centrage desktop est deja juste.
const MOBILE_SHIFT = -0.03;

// Duree du glissement d'une oeuvre a l'autre.
const SLIDE_MS = 620;

// En dessous de ce deplacement, le geste est ignore.
const SWIPE_THRESHOLD = 45;

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const ArtworkSlider = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(HEADER_FALLBACK);
  const [viewportHeight, setViewportHeight] = useState(0);

  const lockRef = useRef(false);
  const timerRef = useRef(null);
  const touchStart = useRef(null);
  const wheelAccum = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const response = await fetch('/data/flowers.json');
        const data = await response.json();
        setArtworks(shuffle(data));
      } catch (error) {
        console.error('Erreur lors du chargement des oeuvres :', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  // Mesure le header rendu plutot que de figer sa hauteur : elle bouge avec la
  // typo et les reglages d'accessibilite du telephone. La barre est le premier
  // enfant du <header>, le wrapper etant vide de hauteur (contenu en fixed).
  useEffect(() => {
    const bar = document.querySelector('header > *');
    if (!bar) return undefined;

    const measure = () => {
      const { height } = bar.getBoundingClientRect();
      if (height > 0) setHeaderHeight(height);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  // Hauteur reellement disponible. visualViewport suit la barre d'outils Safari,
  // que ni vh ni innerHeight ne refletent de facon fiable sur iOS.
  useEffect(() => {
    const measure = () => {
      const vv = window.visualViewport;
      setViewportHeight(vv ? vv.height : window.innerHeight);
    };
    measure();

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', measure);
      vv.addEventListener('scroll', measure);
    }
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', measure);
        vv.removeEventListener('scroll', measure);
      }
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  // La page ne scrolle pas : la navigation se fait par glissement des slides.
  useEffect(() => {
    const { overflow, overscrollBehaviorY } = document.body.style;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehaviorY = 'none';
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.overscrollBehaviorY = overscrollBehaviorY;
    };
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  const total = artworks.length;

  // Avance d'une oeuvre : on translate la pile, puis on recale l'index une fois
  // l'animation finie. Le scroll n'est jamais deplace, donc la boucle n'a ni
  // debut ni fin a franchir et aucun saut ne peut apparaitre.
  const go = useCallback(
    (direction) => {
      if (lockRef.current || total < 2) return;

      lockRef.current = true;
      setAnimating(true);
      setOffset(-direction);

      timerRef.current = window.setTimeout(() => {
        // Les trois etats basculent dans le meme rendu : separes, le navigateur
        // peint une frame intermediaire ou la slide a saute mais la transition
        // est encore active, ce qui se voit comme un clignotement.
        setIndex((prev) => (prev + direction + total) % total);
        setOffset(0);
        setAnimating(false);
        lockRef.current = false;
      }, SLIDE_MS);
    },
    [total]
  );

  // Molette et trackpad. Le verrou evite qu'un geste inertiel enchaine
  // plusieurs oeuvres d'un coup.
  useEffect(() => {
    if (total < 2) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
      if (lockRef.current) return;

      wheelAccum.current += event.deltaY;
      if (Math.abs(wheelAccum.current) < 30) return;

      go(wheelAccum.current > 0 ? 1 : -1);
      wheelAccum.current = 0;
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [go, total]);

  // Clavier : memes deplacements que la molette.
  useEffect(() => {
    if (total < 2) return undefined;

    const onKeyDown = (event) => {
      if (['ArrowDown', 'PageDown', ' '].includes(event.key)) {
        event.preventDefault();
        go(1);
      } else if (['ArrowUp', 'PageUp'].includes(event.key)) {
        event.preventDefault();
        go(-1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [go, total]);

  const onTouchStart = (event) => {
    touchStart.current = event.touches[0].clientY;
  };

  const onTouchMove = (event) => {
    if (touchStart.current === null || lockRef.current) return;

    const delta = touchStart.current - event.touches[0].clientY;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;

    go(delta > 0 ? 1 : -1);
    touchStart.current = null;
  };

  const onTouchEnd = () => {
    touchStart.current = null;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Box
          component="img"
          src={flowerIcon}
          alt="Loading"
          sx={{
            width: '45px',
            height: '45px',
            animation: 'spin 6s linear infinite',
          }}
        />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </Box>
    );
  }

  if (total === 0) {
    return <div>No artworks available</div>;
  }

  // Positif = remonte, negatif = descend. Calcule sur la hauteur reellement
  // visible, la seule fiable sur Safari iOS.
  const mobileShiftPx = Math.round(viewportHeight * MOBILE_SHIFT);

  // Trois slides seulement : precedente, courante, suivante. Le modulo les fait
  // tourner, donc la liste n'a pas de fin.
  const window3 = [-1, 0, 1].map((step) => ({
    art: artworks[(index + step + total) % total],
    step,
  }));

  return (
    <Box
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        // Hauteur mesuree plutot que inset: 0, qui sur iOS deborde sous la
        // barre d'outils et decale tout le contenu vers le bas.
        height: viewportHeight ? `${viewportHeight}px` : '100vh',
        overflow: 'hidden',
        touchAction: 'none',
      }}
    >
      {window3.map(({ art, step }) => (
        <Box
          // Cle liee a l'oeuvre et non a sa position : React reutilise le noeud
          // au lieu de le recreer, sinon l'image se remonte a chaque passage et
          // la slide clignote en fin de mouvement.
          key={art.id ?? art.slug}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, md: 6 },
            // Padding symetrique : le bloc est centre dans la hauteur reelle,
            // header deduit. Le decalage mobile s'ajoute d'un cote ou de
            // l'autre selon son signe, sans jamais rendre un padding negatif.
            pt: {
              xs: `${headerHeight + Math.max(0, -mobileShiftPx) * 2}px`,
              md: `${headerHeight}px`,
            },
            pb: {
              xs: `${headerHeight + Math.max(0, mobileShiftPx) * 2}px`,
              md: `${headerHeight}px`,
            },
            boxSizing: 'border-box',
            transform: `translate3d(0, ${(step + offset) * 100}%, 0)`,
            transition: animating
              ? `transform ${SLIDE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
              : 'none',
            willChange: 'transform',
            // Maintient la slide sur sa propre couche GPU : sans cela Safari la
            // rend a plat en fin de transition et l'image scintille.
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          <Box
            component="img"
            src={`/assets/images/${art.mainImage}`}
            alt={art.title}
            decoding="async"
            onClick={() => navigate(`/artwork/${art.id ?? art.slug}`)}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: '62vh', md: '74vh' },
              objectFit: 'contain',
              cursor: 'pointer',
              display: 'block',
              userSelect: 'none',
            }}
            draggable={false}
          />

          <Box sx={{ mt: { xs: 2.5, md: 3 }, textAlign: 'center', maxWidth: '640px' }}>
            <Typography
              sx={{
                fontFamily: "'Ranade', sans-serif",
                fontWeight: 400,
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.3,
              }}
            >
              {art.title}
              {art.year ? `, ${art.year}` : ''}
            </Typography>

            {art.technique && (
              <Typography
                sx={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 300,
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  opacity: 0.7,
                  mt: 0.5,
                }}
              >
                {art.technique}
              </Typography>
            )}

            {art.dimensions && (
              <Typography
                sx={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 300,
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  opacity: 0.7,
                }}
              >
                {art.dimensions}
              </Typography>
            )}
          </Box>
        </Box>
      ))}

      {/* Puces de progression : reperes de position, desktop uniquement. */}
      <Box
        sx={{
          position: 'absolute',
          right: { xs: 10, md: 22 },
          top: '50%',
          transform: 'translateY(-50%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 1.2,
          zIndex: 5,
        }}
      >
        {artworks.map((art, dotIndex) => (
          <Box
            key={`dot-${art.id ?? art.slug}`}
            onClick={() => {
              if (dotIndex === index || lockRef.current) return;
              go(dotIndex > index ? 1 : -1);
            }}
            role="button"
            aria-label={`Voir ${art.title}`}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              cursor: 'pointer',
              backgroundColor: '#000000',
              opacity: dotIndex === index ? 0.85 : 0.2,
              transition: 'opacity 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ArtworkSlider;
